import React from "react";
import { Helmet } from "react-helmet-async";
// styles dédiés à ce composant (voir ArticlePage.scss)
import "./ArticlePage.scss";
// image de démonstration (même image que EpreuveDetail) ; remplacez-la
// par vos propres visuels lors du branchement à une vraie source de contenu
import DefaultCoverImage from "../medias/calendrier_triathlon.png";

/**
 * Page "article éditorial" réutilisable : sur-titre, titre, chapeau,
 * corps de texte avec inter-titres, photo légendée et un encadré.
 *
 * Ce composant est volontairement générique (props) pour pouvoir être
 * branché plus tard sur une source de données (CMS, API, markdown...).
 * Les valeurs par défaut ci-dessous servent de démonstration/gabarit.
 *
 * Props :
 * - kicker        : sur-titre (ex : "Préparation")
 * - title         : titre principal (h1)
 * - lead          : chapeau (paragraphe d'accroche)
 * - coverImage    : { src, alt, caption }
 * - sections      : [{ heading, paragraphs: [string, ...] }, ...]
 * - sidebar       : { title, items: [string, ...] } ou { title, content }
 * - author        : nom de l'auteur (pour le JSON-LD)
 * - publishedDate : date ISO (ex : "2026-07-20") pour le JSON-LD
 * - canonicalPath : chemin de la page (ex : "/articles/bien-choisir-son-format")
 */
export const ArticlePage = ({
  kicker = "Conseils",
  title = "Bien choisir son format de triathlon",
  lead = "XS, S, M, L, XL... derrière ces lettres se cachent des distances très différentes. Voici comment choisir le format adapté à votre niveau et à vos objectifs de la saison.",
  coverImage = {
    src: DefaultCoverImage,
    alt: "Départ d'une épreuve de triathlon en pleine eau",
    caption: "Le choix du format conditionne toute la préparation.",
  },
  sections = [
    {
      heading: "Partir sur un format court pour découvrir la discipline",
      paragraphs: [
        "Les formats XS et S sont les plus accessibles : ils demandent une préparation de quelques mois et restent proches d'un triathlon découverte. Ils permettent de valider les trois disciplines sans engager une préparation trop lourde.",
        "C'est aussi le format recommandé pour repartir après une blessure ou une longue coupure.",
      ],
    },
    {
      heading: "Le format M, la référence pour progresser",
      paragraphs: [
        "Le format M (distance olympique) reste la référence de la discipline. Il demande une régularité à l'entraînement sur plusieurs mois, avec un volume hebdomadaire plus soutenu que sur un format court.",
      ],
    },
    {
      heading: "L et XL : une autre gestion de l'effort",
      paragraphs: [
        "Sur les longues distances, la gestion de l'alimentation et de l'allure prend le pas sur la pure vitesse. La préparation s'étale généralement sur plusieurs mois, avec une attention particulière portée à la récupération.",
      ],
    },
  ],
  sidebar = {
    title: "À retenir",
    items: [
      "XS / S : idéal pour découvrir ou reprendre la discipline.",
      "M : le format de référence, à partir de 3-4 mois de préparation.",
      "L / XL : nécessite une gestion fine de l'alimentation et de l'allure.",
      "Dans le doute, partez toujours sur un format en dessous de votre ambition initiale.",
    ],
  },
  author = "Rédaction XL Triathlon",
  publishedDate = "2026-07-22",
  canonicalPath = "/articles/bien-choisir-son-format",
}) => {
  const canonicalUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${canonicalPath}`
      : undefined;

  const imageAbsoluteUrl =
    typeof window !== "undefined" && coverImage?.src
      ? `${window.location.origin}${coverImage.src}`
      : coverImage?.src;

  const metaDescription =
    lead.length > 155 ? lead.slice(0, 154).trimEnd() + "…" : lead;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: metaDescription,
    image: imageAbsoluteUrl ? [imageAbsoluteUrl] : undefined,
    datePublished: publishedDate,
    author: {
      "@type": "Organization",
      name: author,
    },
    ...(canonicalUrl ? { mainEntityOfPage: canonicalUrl } : {}),
  };

  return (
    <article className="article-page">
      <Helmet>
        <title>{title} | Calendrier Triathlon</title>
        <meta name="description" content={metaDescription} />
        {canonicalUrl ? <link rel="canonical" href={canonicalUrl} /> : null}

        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        {imageAbsoluteUrl ? (
          <meta property="og:image" content={imageAbsoluteUrl} />
        ) : null}
        {canonicalUrl ? (
          <meta property="og:url" content={canonicalUrl} />
        ) : null}

        <script type="application/ld+json">
          {JSON.stringify(articleJsonLd)}
        </script>
      </Helmet>

      <div className="container">
        {/* ---- En-tête : sur-titre / titre / chapeau ---- */}
        <header className="article-header row justify-content-center">
          <div className="col-lg-9">
            <p className="article-kicker">{kicker}</p>
            <h1 className="article-title">{title}</h1>
            <p className="article-lead">{lead}</p>
          </div>
        </header>

        {/* ---- Photo de couverture ---- */}
        {coverImage?.src ? (
          <figure className="article-cover row justify-content-center">
            <div className="col-lg-10">
              <img
                src={coverImage.src}
                alt={coverImage.alt}
                className="w-100 img-fluid rounded"
                loading="lazy"
              />
              {coverImage.caption ? (
                <figcaption className="article-cover-caption">
                  {coverImage.caption}
                </figcaption>
              ) : null}
            </div>
          </figure>
        ) : null}

        {/* ---- Corps de l'article + encadré ---- */}
        <div className="row justify-content-center">
          <div className="col-lg-9">
            <div className="row">
              <div className="col-lg-8 article-body">
                {sections.map((section, index) => (
                  <section key={index} className="article-section">
                    <h2 className="article-subheading">{section.heading}</h2>
                    {section.paragraphs.map((paragraph, pIndex) => (
                      <p key={pIndex} className="article-paragraph">
                        {paragraph}
                      </p>
                    ))}
                  </section>
                ))}
              </div>

              {/* ---- Encadré : passe sous le texte en mobile/tablette,
                    devient une colonne latérale à partir de "lg" ---- */}
              {sidebar ? (
                <aside className="col-lg-4 article-sidebar-col">
                  <div className="article-sidebar">
                    <div className="article-sidebar-title">
                      {sidebar.title}
                    </div>
                    {sidebar.items ? (
                      <ul className="article-sidebar-list">
                        {sidebar.items.map((item, index) => (
                          <li key={index}>{item}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="article-sidebar-content">
                        {sidebar.content}
                      </p>
                    )}
                  </div>
                </aside>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

export default ArticlePage;
